import { useRef, useState, useEffect, useCallback } from "react";
import { Box, CircularProgress, Button, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { fetch_marketaux_news } from "../../services/apis";
import NewsCard from "../newsCard/NewsCard";
import SEO from "../../components/SEO/SEO";
import { getSeoData } from "../../utils/seoConfig";

// --- NEWS VIEWER COMPONENT (InShorts-style) ---
const NewsViewer = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const containerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [newsData, setNewsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const seoData = getSeoData("/news");
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  // const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10; // Items per page - fixed value to prevent re-renders
  
  // Touch handling states
  const touchStartY = useRef(0);
  const touchEndY = useRef(0);
  const isDragging = useRef(false);
  const hasMoved = useRef(false);
  const [dragOffset, setDragOffset] = useState(0);

  // Handle touch/mouse start
  const handleTouchStart = useCallback((e) => {
    if (isDesktop) return;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    touchStartY.current = clientY;
    touchEndY.current = clientY;
    isDragging.current = true;
    hasMoved.current = false; // Reset movement flag
  }, [isDesktop]);

  // Handle touch/mouse move
  const handleTouchMove = useCallback((e) => {
    if (isDesktop || !isDragging.current) return;
    
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    touchEndY.current = clientY;
    const diff = touchEndY.current - touchStartY.current;
    
    // Mark as moved if moved more than 5 pixels (distinguish from click)
    if (Math.abs(diff) > 5) {
      hasMoved.current = true;
      e.preventDefault(); // Only prevent default if actually moving
    }
    
    // Apply resistance at boundaries
    const isAtTop = activeIndex === 0 && diff > 0;
    const isAtBottom = activeIndex === newsData.length - 1 && diff < 0;
    
    if (isAtTop || isAtBottom) {
      setDragOffset(diff * 0.3); // Apply resistance
    } else {
      setDragOffset(diff);
    }
  }, [isDesktop, activeIndex, newsData.length]);

  // Handle touch/mouse end
  const handleTouchEnd = useCallback(() => {
    if (isDesktop || !isDragging.current) return;
    
    isDragging.current = false;
    
    // Only change card if there was actual movement
    if (hasMoved.current) {
      const diff = touchEndY.current - touchStartY.current;
      const threshold = 50; // Minimum swipe distance

      if (Math.abs(diff) > threshold) {
        if (diff > 0 && activeIndex > 0) {
          // Swipe down - go to previous
          setActiveIndex(prev => prev - 1);
        } else if (diff < 0 && activeIndex < newsData.length - 1) {
          // Swipe up - go to next
          setActiveIndex(prev => prev + 1);
        }
      }
    }
    
    // Reset drag offset and flags
    setDragOffset(0);
    touchStartY.current = 0;
    touchEndY.current = 0;
    hasMoved.current = false;
  }, [isDesktop, activeIndex, newsData.length]);

  // Wheel event handling with proper debouncing
  const lastWheelTime = useRef(0);
  const wheelTimeout = useRef(null);
  const accumulatedDelta = useRef(0);

  const handleWheel = useCallback((e) => {
    if (isDesktop || isDragging.current) return;
    
    e.preventDefault();
    e.stopPropagation();

    const now = Date.now();
    const timeSinceLastWheel = now - lastWheelTime.current;

    // Reset accumulated delta if too much time has passed
    if (timeSinceLastWheel > 500) {
      accumulatedDelta.current = 0;
    }

    // Accumulate delta
    accumulatedDelta.current += e.deltaY;

    // Clear previous timeout
    if (wheelTimeout.current) {
      clearTimeout(wheelTimeout.current);
    }

    // Wait for wheel to stop before acting
    wheelTimeout.current = setTimeout(() => {
      const threshold = 100; // Higher threshold to prevent accidental scrolls
      
      if (Math.abs(accumulatedDelta.current) > threshold) {
        if (accumulatedDelta.current > 0 && activeIndex < newsData.length - 1) {
          setActiveIndex(prev => prev + 1);
          lastWheelTime.current = now;
        } else if (accumulatedDelta.current < 0 && activeIndex > 0) {
          setActiveIndex(prev => prev - 1);
          lastWheelTime.current = now;
        }
      }
      
      accumulatedDelta.current = 0;
    }, 150); // Wait 150ms after wheel stops
  }, [isDesktop, activeIndex, newsData.length]);

  // Fetch initial news data
  useEffect(() => {
    const fetchNews = async () => {
      try {
        const res = await fetch_marketaux_news({ page: 1, page_size: pageSize });
        if (res && res.data) {
          setNewsData(res.data);
          setHasMore(res.pagination.has_more);
          // setTotalPages(res.pagination.total_pages);
          setCurrentPage(1);
        }
      } catch (err) {
        console.error("Error fetching news:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only fetch once on mount

  // Load more news
  const handleLoadMore = useCallback(async () => {
    if (!hasMore || loadingMore) return;
    
    setLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const res = await fetch_marketaux_news({ page: nextPage, page_size: pageSize });
      
      if (res && res.data) {
        setNewsData(prev => [...prev, ...res.data]);
        setHasMore(res.pagination.has_more);
        setCurrentPage(nextPage);
        // setTotalPages(res.pagination.total_pages);
      }
    } catch (err) {
      console.error("Error loading more news:", err);
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, currentPage, pageSize]);

  // Auto-load more on mobile when near the end
  useEffect(() => {
    // Only trigger on mobile when user is near the end (3 items before the end)
    if (!isDesktop && activeIndex >= newsData.length - 3 && hasMore && !loadingMore && newsData.length > 0) {
      handleLoadMore();
    }
  }, [activeIndex, newsData.length, hasMore, loadingMore, isDesktop, handleLoadMore]);

  // Setup wheel event listener for mobile view
  useEffect(() => {
    if (!containerRef.current || isDesktop) return;
    
    const container = containerRef.current;
    
    container.addEventListener("wheel", handleWheel, { passive: false });
    
    return () => {
      container.removeEventListener("wheel", handleWheel);
      if (wheelTimeout.current) {
        clearTimeout(wheelTimeout.current);
      }
    };
  }, [handleWheel, isDesktop]);

  return (
    <>
      <SEO {...seoData} />
      <Box
        ref={containerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleTouchStart}
        onMouseMove={handleTouchMove}
        onMouseUp={handleTouchEnd}
        onMouseLeave={handleTouchEnd}
        sx={{
          height: "100vh",
        overflow: isDesktop ? "auto" : "hidden",
        position: "relative",
        width: "100vw",
        bgcolor: theme.palette.background.default,
        touchAction: "none",
        cursor: !isDesktop ? (isDragging.current ? "grabbing" : "grab") : "auto",
        // userSelect: "none",
        
      }}
    >
      {loading ? (
        <Box height="100vh" display="flex" justifyContent="center" alignItems="center">
          <CircularProgress />
        </Box>
      ) : isDesktop ? (
        <Box sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          p: 3,
          minHeight: "100vh",
          width: "100%"
        }}>
          {newsData.map((news, idx) => (
            <Box sx={{width:'100%',mb:1}} key={idx} >
              <NewsCard news={news} />
            </Box>
          ))}
          {hasMore && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2, mb: 4 }}>
              <Button 
                variant="contained" 
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? <CircularProgress size={24} /> : `Load More`}
              </Button>
            </Box>
          )}
          {!hasMore && newsData.length > 0 && (
            <Box sx={{ textAlign: "center", py: 3, color: "text.secondary" }}>
              You've reached the end of the news
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{ 
          height: "100%", 
          width: "100%",
          transform: `translateY(calc(-${activeIndex * 100}vh + ${dragOffset}px))`,
          transition: isDragging.current ? "none" : "transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
          willChange: "transform"
        }}>
          {newsData.map((news, index) => (
            <Box
              key={index}
              sx={{
                height: "100vh",
                width: "100vw",
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
              }}
            >
              <NewsCard news={news} />
            </Box>
          ))}
          
          {/* Loading indicator for mobile infinite scroll */}
          {loadingMore && (
            <Box
              sx={{
                height: "100vh",
                width: "100vw",
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
              }}
            >
              <CircularProgress />
            </Box>
          )}
        </Box>
      )}
    </Box>
    </>
  );
};

export default NewsViewer;
